/**
 * <rz-recommendations data-url>: loads Shopify product recommendations when the element
 * scrolls near the viewport. The URL renders a section; the matching [data-recommendations]
 * element from the response replaces this one's content (empty result = element removed).
 *
 * <rz-recently-viewed data-section-id data-limit data-exclude>: renders the products stored
 * by product pages in localStorage (newest first) via a search for their ids.
 */
// Wrapped: several sections include this file on one page; top-level consts would be declared twice.
(() => {
  const RZ_RECENT_KEY = 'rz-recently-viewed';

  const rzReadRecent = (key = RZ_RECENT_KEY) => {
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (e) {
      return [];
    }
  };

  // The nearest ancestor that actually has a box. The product page's main section and its wrappers
  // are display: contents (no box, never intersects), and a block waiting for results is still [hidden].
  const rzObservableAncestor = (element) => {
    for (let el = element.parentElement; el; el = el.parentElement) {
      const { display } = getComputedStyle(el);
      if (display !== 'contents' && display !== 'none') return el;
    }
    return element;
  };

  const rzLoadWhenNear = (element, load) => {
    // Inside a drawer (<dialog>) the section never scrolls into view: load when the dialog opens.
    const dialog = element.closest('dialog');
    if (dialog) {
      if (dialog.open) return load();
      const watcher = new MutationObserver(() => {
        if (dialog.open) {
          watcher.disconnect();
          load();
        }
      });
      watcher.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(rzObservableAncestor(element));
  };

  const rzProductPath = (link) => new URL(link.getAttribute('href'), location.origin).pathname;

  // Products the "Complementary products" block on this page shows. Shopify's related results
  // repeat them, so the related section drops them. Not fatal: at worst a product shows twice.
  const rzComplementaryPaths = async () => {
    const block = document.querySelector('.complementary rz-recommendations');
    if (!block) return new Set();
    try {
      const html = await fetch(block.dataset.url).then((r) => r.text());
      const links = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelectorAll(`[data-recommendations="${block.dataset.recommendations}"] a[href*="/products/"]`);
      return new Set([...links].map(rzProductPath));
    } catch {
      return new Set();
    }
  };

  const rzSkipComplementary = async (fresh, limit) => {
    const shownAbove = await rzComplementaryPaths();
    let kept = 0;
    // Top-level items only: a product card can contain lists of its own.
    const items = [...fresh.querySelectorAll('li')].filter((li) => !li.parentElement.closest('li'));
    items.forEach((item) => {
      const link = item.querySelector('a[href*="/products/"]');
      if ((link && shownAbove.has(rzProductPath(link))) || kept >= limit) item.remove();
      else kept++;
    });
  };

  const rzSwapFrom = async (element, url) => {
    const html = await fetch(url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(`[data-recommendations="${element.dataset.recommendations}"]`);
    if (fresh && 'skipComplementary' in element.dataset) {
      await rzSkipComplementary(fresh, Number(element.dataset.limit));
    }
    if (!fresh || !fresh.querySelector('li')) {
      element.closest('[data-hide-when-empty]')?.setAttribute('hidden', '');
      element.remove();
      return;
    }
    element.innerHTML = fresh.innerHTML;
    element.closest('[data-hide-when-empty]')?.removeAttribute('hidden');
  };

  if (!customElements.get('rz-recommendations')) {
    customElements.define(
      'rz-recommendations',
      class extends HTMLElement {
        connectedCallback() {
          rzLoadWhenNear(this, () => rzSwapFrom(this, this.dataset.url));
        }
      }
    );
  }

  if (!customElements.get('rz-recently-viewed')) {
    customElements.define(
      'rz-recently-viewed',
      class extends HTMLElement {
        connectedCallback() {
          const exclude = Number(this.dataset.exclude || 0);
          // data-key="rz-wishlist" reuses this element for the wishlist page.
          const ids = rzReadRecent(this.dataset.key)
            .filter((id) => id !== exclude)
            .slice(0, Number(this.dataset.limit || 8));
          if (!ids.length) {
            this.closest('[data-hide-when-empty]')?.setAttribute('hidden', '');
            return;
          }
          const q = ids.map((id) => `id:${id}`).join(' OR ');
          const root = window.Shopify?.routes?.root || '/';
          const url = `${root}search?type=product&q=${encodeURIComponent(q)}&section_id=${this.dataset.sectionId}`;
          rzLoadWhenNear(this, async () => {
            await rzSwapFrom(this, url);
            // Search returns relevance order; show newest-viewed first.
            const track = this.querySelector('[data-track]');
            ids.forEach((id) => {
              const item = track?.querySelector(`li[data-id="${id}"]`);
              if (item) track.append(item);
            });
          });
        }
      }
    );
  }
})();
